/**
 * Copyright © 2026 Adnuntius AS.
 */
import _ from 'lodash';

import { AdminResourceEditRoutePageComponent } from './admin-resource-edit-route-page.component';
import { AdminResourceContextRoutePageComponent } from './admin-resource-context-route-page.component';
import { AdminResourceItemRoutePageComponent } from './admin-resource-item-route-page.component';
import { ResourceListRoutePageComponent } from './resource-list-route-page.component';
import {
  RouteTwoPanelLayoutComponent
} from '../directives/route-two-panel-layout.component';

const ROUTE_LIST_PAGE_RESOLVE = 'listPageRouteKey';
const ROUTE_ITEM_KEY_RESOLVE = 'adminResourceItemKey';
const ROUTE_ITEM_VARIANT_RESOLVE = 'adminResourceItemVariant';

export function listPageRouteResolve(listPageKey) {
  const resolve = {};
  resolve[ROUTE_LIST_PAGE_RESOLVE] = function() {
    return listPageKey;
  };
  return resolve;
}

export function adminResourceItemRouteResolve(itemKey, variant) {
  variant = variant || 'item';
  const resolve = {};
  resolve[ROUTE_ITEM_KEY_RESOLVE] = function() {
    return itemKey;
  };
  resolve[ROUTE_ITEM_VARIANT_RESOLVE] = function() {
    return variant;
  };
  return resolve;
}

export function adminResourceEditRouteView() {
  return {
    component: AdminResourceEditRoutePageComponent,
    bindings: {edit: 'adminResourceEditRouteKey'}
  };
}

export function adminResourceEditRouteResolve(editKey) {
  return {
    adminResourceEditRouteKey: function() {
      return editKey;
    }
  };
}

export function adminResourceContextRouteView() {
  return {
    component: AdminResourceContextRoutePageComponent
  };
}

export function adminResourceItemRouteView() {
  return {
    component: AdminResourceItemRoutePageComponent,
    bindings: {
      item: ROUTE_ITEM_KEY_RESOLVE,
      variant: ROUTE_ITEM_VARIANT_RESOLVE
    }
  };
}

export function resourceListRouteView() {
  return {
    component: ResourceListRoutePageComponent,
    bindings: {listPage: ROUTE_LIST_PAGE_RESOLVE}
  };
}

function editTagToRegistryKey(editTag) {
  return editTag.replace(/^adn-/, '').replace(/-edit-page$/, '-edit');
}

function listTagToRegistryKey(listTag) {
  const tag = listTag.indexOf('adn-') === 0 ? listTag : 'adn-' + listTag;
  return tag.replace(/^adn-/, '').replace(/-list-page$/, '-list');
}

function buildEditContentView() {
  return adminResourceItemRouteView();
}

function buildEditContentResolve(editTag) {
  return adminResourceItemRouteResolve(editTagToRegistryKey(editTag), 'edit');
}

function rightTagToEditKey(rightTag) {
  const tag = rightTag.indexOf('adn-') === 0 ? rightTag : 'adn-' + rightTag;
  if (tag.indexOf('-edit-page') < 0 && tag.indexOf('-page') > -1) {
    return tag.replace(/^adn-/, '').replace(/-page$/, '-edit');
  }
  return editTagToRegistryKey(tag);
}

function buildItemViews(config, contextView, rh) {
  const contentView = buildEditContentView();
  if (config.twoPanel) {
    const itemState = rh.itemPath();
    return {
      'content@app': {
        component: RouteTwoPanelLayoutComponent,
        bindings: {full: 'full'}
      },
      'context@app': contextView,
      [config.twoPanel.leftView || ('leftPanel@' + itemState)]: contentView,
      [config.twoPanel.rightView || ('rightPanel@' + itemState)]: config.twoPanel.rightTag ? {
        component: AdminResourceItemRoutePageComponent,
        bindings: {
          item: 'rightPanelItemKey',
          variant: ROUTE_ITEM_VARIANT_RESOLVE
        }
      } : {}
    };
  }

  return {
    'content@app': contentView,
    'context@app': contextView
  };
}

export function configureAdminResourceListOnly($stateProvider, rh, config) {
  const listKey = listTagToRegistryKey(config.listTag);
  const listExtras: any = {
    views: _.merge({
      'content@app': resourceListRouteView()
    }, config.listViews || {}),
    resolve: _.merge({}, config.listResolve || {}, listPageRouteResolve(listKey))
  };

  if (config.listData) {
    listExtras.data = config.listData;
  }

  $stateProvider.state(rh.listPath(), rh.list(listExtras));
}

export function configureAdminResourceStates($stateProvider, rh, config) {
  const listKey = config.listTag ? listTagToRegistryKey(config.listTag) : null;
  const listExtras: any = config.listTag ? {
    views: {
      'content@app': resourceListRouteView()
    },
    resolve: _.merge({}, config.listResolve || {}, listPageRouteResolve(listKey))
  } : {
    resolve: config.listResolve || {}
  };

  if (config.listData) {
    listExtras.data = config.listData;
  }

  const contextView = adminResourceContextRouteView();

  const itemConfig: any = {
    views: buildItemViews(config, contextView, rh)
  };
  itemConfig.resolve = _.merge(
    {},
    config.itemResolve || {},
    buildEditContentResolve(config.editTag)
  );
  if (config.twoPanel && config.twoPanel.rightTag) {
    itemConfig.resolve.leftPanelItemKey = function() {
      return editTagToRegistryKey(config.editTag);
    };
    itemConfig.resolve.rightPanelItemKey = function() {
      return rightTagToEditKey(config.twoPanel.rightTag);
    };
    itemConfig.resolve[ROUTE_ITEM_VARIANT_RESOLVE] = function() {
      return 'edit';
    };
  }
  if (config.twoPanel && config.twoPanel.rightTag) {
    const itemState = rh.itemPath();
    const leftView = config.twoPanel.leftView || ('leftPanel@' + itemState);
    itemConfig.views[leftView] = {
      component: AdminResourceItemRoutePageComponent,
      bindings: {item: 'leftPanelItemKey', variant: ROUTE_ITEM_VARIANT_RESOLVE}
    };
  }
  if (config.itemData) {
    itemConfig.data = config.itemData;
  }

  if (config.listTag) {
    $stateProvider.state(rh.listPath(), rh.list(listExtras));
  }
  $stateProvider.state(rh.newItemPath(), rh.newItem())
    .state(rh.itemPath(), rh.modelCommsItem(itemConfig, config.objectType, config.crumbParams));
}
